import React, { useEffect, useState } from "react";
import { Box, Grid2, Typography, Divider, CircularProgress } from "@mui/material";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import FullScreenDialog from "../../../../../Components/FullScreenDialog";
import CloseButton from "../../../../../Components/CloseButton";
import CustomIcon from "../../../../../Components/CustomIcon";
import { getBatteryServiceRecords } from "../../../../../Api/ServiceRecordApi";
import { buildEventHistoryEntries } from "../../../../../Helper/DigitalTwin/BatteryComponentsHelper";
import { getLocalDateFromUTC } from "../../../../../Helper/DatePicker/DateConverter";

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: "Unknown date", time: "" };
    try {
        return {
            date: getLocalDateFromUTC(dateTimeString, "DD MMMM YYYY"),
            time: getLocalDateFromUTC(dateTimeString, "h:mm A"),
        };
    } catch (e) {
        return { date: "Unknown date", time: "" };
    }
};

// Group flat entries by formatted local date, preserving newest-first order.
const groupByDate = (entries) => {
    const groups = [];
    const indexByDate = {};
    for (const entry of entries) {
        const { date, time } = formatDateTime(entry.recordedAt);
        if (indexByDate[date] === undefined) {
            indexByDate[date] = groups.length;
            groups.push({ date, events: [] });
        }
        groups[indexByDate[date]].events.push({ ...entry, time });
    }
    return groups;
};

const InfoRow = ({ label, children }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: "6px" }}>
        <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>{label}</Typography>
        <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#2E2E2E", textAlign: "right" }}>{children}</Box>
    </Box>
);

const Chip = ({ children }) => (
    <Box
        component="span"
        sx={{
            display: "inline-block",
            fontSize: "12px",
            fontWeight: 500,
            color: "#4A4AF4",
            backgroundColor: "#EEEFFF",
            borderRadius: "6px",
            padding: "2px 10px",
            ml: "6px",
        }}
    >
        {children}
    </Box>
);

// Right slide-over showing pack replacement history. absPackNo null =>
// battery-level (all packs); otherwise scoped to that physical slot (grouping by
// the stable absPackNo captures the slot's FULL history across replacements).
export default function EventHistoryDrawer({
    open,
    onClose,
    batteryID,
    absPackNo = null,
    chipLabel = "",
    vehicleNumber = "",
    title = "Replacement History",
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [groups, setGroups] = useState([]);

    useEffect(() => {
        if (!open || !batteryID) return;
        let active = true;
        (async () => {
            setLoading(true);
            setError(false);
            try {
                const res = await getBatteryServiceRecords(batteryID, {
                    pageSize: 500,
                    sortBy: "recordedAt",
                    order: "desc",
                });
                if (!active) return;
                const records = res?.response?.data || [];
                setGroups(groupByDate(buildEventHistoryEntries(records, absPackNo)));
            } catch (err) {
                console.error("Error fetching replacement history:", err);
                if (active) setError(true);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [open, batteryID, absPackNo]);

    const isEmpty = !loading && !error && groups.length === 0;

    return (
        <FullScreenDialog
            open={!!open}
            handleClose={onClose}
            containerStyles={{ overflowY: "hidden", overflowX: "hidden", ml: "auto", width: "calc(100%/3)" }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ p: 3, flexShrink: 0 }}>
                    <Grid2 container justifyContent="space-between" alignItems="center" wrap="nowrap">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <CustomIcon name="history-icon" fill="#686868" width="20" height="20" />
                            <Typography sx={{ fontSize: "20px", lineHeight: "20px", color: "#101111", fontWeight: 600 }}>
                                {title}
                            </Typography>
                            {chipLabel && (
                                <Box
                                    sx={{
                                        fontSize: "12px",
                                        color: "#494949",
                                        backgroundColor: "#F0F0F0",
                                        borderRadius: "6px",
                                        padding: "2px 10px",
                                    }}
                                >
                                    {chipLabel}
                                </Box>
                            )}
                            {vehicleNumber && (
                                <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>{vehicleNumber}</Typography>
                            )}
                        </Box>
                        <CloseButton onClick={onClose} />
                    </Grid2>
                    <Divider sx={{ borderColor: "#E6E6E6", mt: 2, mx: -3 }} />
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        px: 3.5,
                        pb: 3,
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "3px" },
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : error ? (
                        <Typography sx={{ textAlign: "center", mt: 4, fontSize: "14px", color: "#E5484D" }}>
                            Failed to load replacement history
                        </Typography>
                    ) : isEmpty ? (
                        <Typography sx={{ textAlign: "center", mt: 4, fontSize: "14px", color: "#A2A2A2" }}>
                            No replacement history available
                        </Typography>
                    ) : (
                        groups.map((group, gi) => (
                            <Box key={gi} sx={{ mb: 3 }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#000", lineHeight: "24px", mb: 2 }}>
                                    {group.date}
                                </Typography>
                                {group.events.map((event) => (
                                    <Box key={event.id} sx={{ mb: 3, display: "flex", gap: 2 }}>
                                        <Typography sx={{ fontSize: "13px", color: "#8F8F8F", minWidth: "64px" }}>
                                            {event.time}
                                        </Typography>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: "14px", color: "#494949", mb: "4px" }}>
                                                <span style={{ color: "#4A4AF4", fontWeight: 600 }}>
                                                    @{event.actorName || "Unknown"}
                                                </span>{" "}
                                                replaced the battery pack
                                            </Typography>

                                            <InfoRow label="Old Serial number">{event.oldSerial || "-"}</InfoRow>
                                            <InfoRow label="New Serial number">{event.newSerial || "-"}</InfoRow>

                                            {event.reasons.length > 0 && (
                                                // Stacked (label above, chips wrapping left-aligned below)
                                                // so the label never squeezes to two lines and any number
                                                // of problem chips lay out cleanly.
                                                <Box sx={{ mt: "8px" }}>
                                                    <Typography sx={{ fontSize: "13px", color: "#8D8D8D", mb: "6px" }}>
                                                        Problem reported
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {event.reasons.map((r, i) => (
                                                            <Chip key={i}>{r}</Chip>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}

                                            {event.remark && (
                                                <Box sx={{ mt: 1.5, bgcolor: "#F9F9F9", p: 1.5, borderRadius: "8px", display: "flex", gap: 1.5 }}>
                                                    <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 16, color: "#8D8D8D", mt: "2px" }} />
                                                    <Box>
                                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#2E2E2E", mb: 0.5 }}>
                                                            Remark:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "13px", color: "#2E2E2E" }}>
                                                            {event.remark}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ))
                    )}
                </Box>
            </Box>
        </FullScreenDialog>
    );
}
